import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Typography,
  Form,
  Input,
  Button,
  Space,
  Select,
  Row,
  Col,
  message,
  Checkbox,
  Spin,
  Alert,
  Divider,
} from 'antd';
import { CodeOutlined, FormOutlined, SaveOutlined, CopyOutlined } from '@ant-design/icons';
import PageHeader from '../../components/common/PageHeader';
import datateamCreate, { CreateDemandParams } from '../../services/datateamCreate';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/**
 * Página para criar demandas no DataTeam.
 * 
 * Permite criar demandas selecionando ambiente, frequência, CNJs e credenciais de segredo de justiça.
 * Suporta modo formulário e modo JSON.
 */
const DatateamNovoContent: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState<'form' | 'json'>('form');
  const [jsonValue, setJsonValue] = useState<string>('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Validar e processar JSON
  const validateJson = (jsonString: string): { valid: boolean; data?: CreateDemandParams; error?: string } => {
    if (!jsonString || jsonString.trim().length === 0) {
      return { valid: false, error: 'JSON não pode estar vazio' };
    }

    try {
      const parsed = JSON.parse(jsonString);
      
      // Validar campos obrigatórios
      if (!parsed.environment || !['saj', 'projuris', 'peticiona'].includes(parsed.environment)) {
        return { valid: false, error: 'Campo "environment" é obrigatório e deve ser "saj", "projuris" ou "peticiona"' };
      }
      if (!parsed.method || typeof parsed.method !== 'object') {
        return { valid: false, error: 'Campo "method" é obrigatório e deve ser um objeto' };
      }
      if (parsed.method.action !== 'insert') {
        return { valid: false, error: 'method.action deve ser "insert"' };
      }
      if (!parsed.method.idCallback || typeof parsed.method.idCallback !== 'string') {
        return { valid: false, error: 'method.idCallback é obrigatório e deve ser uma string' };
      }
      if (!parsed.method.updateFrequency || !['daily', 'never'].includes(parsed.method.updateFrequency)) {
        return { valid: false, error: 'method.updateFrequency é obrigatório e deve ser "daily" ou "never"' };
      }
      if (!parsed.method.values || !Array.isArray(parsed.method.values) || parsed.method.values.length === 0) {
        return { valid: false, error: 'method.values é obrigatório e deve ser um array não vazio' };
      }

      return { valid: true, data: parsed as CreateDemandParams };
    } catch (e: any) {
      return { valid: false, error: `JSON inválido: ${e.message}` };
    }
  };

  // Formatar JSON
  const beautifyJson = () => {
    const validation = validateJson(jsonValue);
    if (validation.valid && validation.data) {
      setJsonValue(JSON.stringify(validation.data, null, 2));
      setJsonError(null);
    } else {
      message.warning('Corrija os erros do JSON antes de formatar');
    }
  };

  // Copiar exemplo de JSON
  const copyExample = () => {
    const example: CreateDemandParams = {
      environment: 'saj',
      idArtifact: 0,
      method: {
        action: 'insert',
        idCallback: 'supreq',
        updateFrequency: 'never',
        values: ['0000000-00.0000.0.00.0000'],
      },
    };
    const exampleJson = JSON.stringify(example, null, 2);
    navigator.clipboard.writeText(exampleJson).then(() => {
      message.success('Exemplo copiado para a área de transferência!');
    }).catch(() => {
      message.error('Erro ao copiar exemplo');
    });
  };

  // Criar demanda a partir do JSON
  const handleSubmitJson = async () => {
    const validation = validateJson(jsonValue);
    
    if (!validation.valid) {
      setJsonError(validation.error || 'JSON inválido');
      message.error(validation.error || 'JSON inválido');
      return;
    }

    setJsonError(null);
    setSubmitting(true);

    try {
      // Se urlCallback estiver vazio, usa "localhost" como padrão
      const data = validation.data!;
      if (!data.urlCallback || !data.urlCallback.trim()) {
        data.urlCallback = 'localhost';
      }
      if (!data.method.urlCallback || !data.method.urlCallback.trim()) {
        data.method.urlCallback = 'localhost';
      }

      const response = await datateamCreate.create(data);

      if (response.success && response.idDemanda) {
        message.success(`Demanda criada com sucesso! ID: ${response.idDemanda}`);
        setJsonValue('');
        
        // Adicionar ID ao localStorage e redirecionar para monitoramento
        const storedIds = localStorage.getItem("datateamVisualizarIds");
        let demandIds: string[] = [];
        if (storedIds) {
          try {
            const parsed = JSON.parse(storedIds);
            if (Array.isArray(parsed)) {
              demandIds = parsed;
            }
          } catch (e) {
            console.error("Erro ao carregar IDs do localStorage:", e);
          }
        }
        
        // Adicionar novo ID se não existir
        if (!demandIds.includes(response.idDemanda)) {
          demandIds.push(response.idDemanda);
          localStorage.setItem("datateamVisualizarIds", JSON.stringify(demandIds));
        }
        
        // Redirecionar para monitoramento
        navigate("/datateam/visualizar/monitoring");
      } else {
        message.error(response.message || 'Erro ao criar demanda');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro desconhecido';
      message.error('Erro ao criar demanda: ' + errorMessage);
      setJsonError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Criar demanda a partir do formulário
  const handleSubmit = async (values: any) => {
    const {
      environment,
      idArtifact = 0,
      idCallback = 'supreq',
      updateFrequency,
      cnjs,
      urlCallback,
      useSecret,
      arn,
      codigoCredentialOrgaoSistema,
      login,
      sistema,
    } = values;

    // Parse CNJs (pode ser string separada por linhas ou JSON array)
    let cnjArray: string[] = [];
    if (typeof cnjs === 'string') {
      try {
        // Tenta parsear como JSON primeiro
        const parsed = JSON.parse(cnjs);
        if (Array.isArray(parsed)) {
          cnjArray = parsed;
        } else {
          // Se não for array, trata como string separada por linhas
          cnjArray = cnjs
            .split('\n')
            .map((line: string) => line.trim())
            .filter((line: string) => line.length > 0);
        }
      } catch {
        // Se não for JSON válido, trata como string separada por linhas
        cnjArray = cnjs
          .split('\n')
          .map((line: string) => line.trim())
          .filter((line: string) => line.length > 0);
      }
    } else if (Array.isArray(cnjs)) {
      cnjArray = cnjs;
    }

    if (cnjArray.length === 0) {
      message.error('Forneça pelo menos um CNJ');
      return;
    }

    setSubmitting(true);

    try {
      // Se urlCallback estiver vazio, usa "localhost" como padrão
      const finalUrlCallback = (urlCallback && urlCallback.trim()) || 'localhost';

      const method: CreateDemandParams['method'] = {
        action: 'insert',
        idCallback,
        updateFrequency,
        values: cnjArray,
        urlCallback: finalUrlCallback,
        ...(useSecret && arn && codigoCredentialOrgaoSistema && login && sistema && {
          credentials: {
            arn,
            codigoCredentialOrgaoSistema,
            login,
            sistema,
          },
        }),
      };

      const params: CreateDemandParams = {
        environment,
        idArtifact: Number(idArtifact) || 0,
        method,
        urlCallback: finalUrlCallback,
      };

      const response = await datateamCreate.create(params);

      if (response.success && response.idDemanda) {
        message.success(`Demanda criada com sucesso! ID: ${response.idDemanda}`);
        form.resetFields();
        
        // Adicionar ID ao localStorage e redirecionar para monitoramento
        const storedIds = localStorage.getItem("datateamVisualizarIds");
        let demandIds: string[] = [];
        if (storedIds) {
          try {
            const parsed = JSON.parse(storedIds);
            if (Array.isArray(parsed)) {
              demandIds = parsed;
            }
          } catch (e) {
            console.error("Erro ao carregar IDs do localStorage:", e);
          }
        }
        
        // Adicionar novo ID se não existir
        if (!demandIds.includes(response.idDemanda)) {
          demandIds.push(response.idDemanda);
          localStorage.setItem("datateamVisualizarIds", JSON.stringify(demandIds));
        }
        
        // Redirecionar para monitoramento
        navigate("/datateam/visualizar/monitoring");
      } else {
        message.error(response.message || 'Erro ao criar demanda');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro desconhecido';
      message.error('Erro ao criar demanda: ' + errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Atualizar JSON quando o formulário muda (opcional, para preview)
  const updateJsonPreview = useCallback(() => {
    const values = form.getFieldsValue();
    const {
      environment,
      idArtifact = 0,
      idCallback = 'supreq',
      updateFrequency,
      cnjs,
      urlCallback,
      useSecret,
      arn,
      codigoCredentialOrgaoSistema,
      login,
      sistema,
    } = values;

    if (!environment || !updateFrequency || !cnjs) {
      return;
    }

    let cnjArray: string[] = [];
    if (typeof cnjs === 'string') {
      try {
        const parsed = JSON.parse(cnjs);
        if (Array.isArray(parsed)) {
          cnjArray = parsed;
        } else {
          cnjArray = cnjs
            .split('\n')
            .map((line: string) => line.trim())
            .filter((line: string) => line.length > 0);
        }
      } catch {
        cnjArray = cnjs
          .split('\n')
          .map((line: string) => line.trim())
          .filter((line: string) => line.length > 0);
      }
    }

    const jsonData: CreateDemandParams = {
      environment,
      idArtifact: Number(idArtifact) || 0,
      method: {
        action: 'insert',
        idCallback,
        updateFrequency,
        values: cnjArray,
        urlCallback: (urlCallback && urlCallback.trim()) || 'localhost',
        ...(useSecret && arn && codigoCredentialOrgaoSistema && login && sistema && {
          credentials: {
            arn,
            codigoCredentialOrgaoSistema,
            login,
            sistema,
          },
        }),
      },
      urlCallback: (urlCallback && urlCallback.trim()) || 'localhost',
    };

    setJsonValue(JSON.stringify(jsonData, null, 2));
  }, [form]);

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <PageHeader
          title="Novo DT"
          subtitle="Cadastre uma nova demanda no DataTeam. Selecione o ambiente, frequência, CNJs e credenciais de segredo de justiça (se necessário)."
        />

        {/* Toggle entre Form e JSON */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Button.Group>
            <Button
              type={mode === 'form' ? 'primary' : 'default'}
              icon={<FormOutlined />}
              onClick={() => setMode('form')}
            >
              Formulário
            </Button>
            <Button
              type={mode === 'json' ? 'primary' : 'default'}
              icon={<CodeOutlined />}
              onClick={() => {
                setMode('json');
                updateJsonPreview();
              }}
            >
              JSON
            </Button>
          </Button.Group>
        </div>

        {mode === 'form' ? (
          <Form
            form={form}
            onFinish={handleSubmit}
            layout="vertical"
            initialValues={{
              idArtifact: 0,
              idCallback: 'supreq',
              updateFrequency: 'never',
              useSecret: false,
            }}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="environment"
                  label="Ambiente"
                  rules={[{ required: true, message: 'Selecione o ambiente' }]}
                >
                  <Select placeholder="Selecione o ambiente">
                    <Option value="saj">SAJ</Option>
                    <Option value="projuris">Projuris</Option>
                    <Option value="peticiona">Peticiona</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="idArtifact"
                  label="ID Artifact"
                  initialValue={0}
                >
                  <Input type="number" disabled />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="updateFrequency"
                  label="Frequência"
                  rules={[{ required: true, message: 'Selecione a frequência' }]}
                >
                  <Select placeholder="Selecione a frequência">
                    <Option value="daily">Daily</Option>
                    <Option value="never">Never</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="idCallback"
                  label="ID Callback"
                  initialValue="supreq"
                  rules={[{ required: true, message: 'Digite o ID do callback' }]}
                >
                  <Input placeholder="ID do callback" disabled />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="urlCallback"
                  label="URL Callback (opcional)"
                >
                  <Input placeholder="URL do callback" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="cnjs"
              label="CNJs"
              rules={[{ required: true, message: 'Digite pelo menos um CNJ' }]}
              extra="Digite um CNJ por linha ou um array JSON: [&quot;cnj1&quot;, &quot;cnj2&quot;]"
            >
              <TextArea
                rows={6}
                placeholder="0000000-00.0000.0.00.0000&#10;0000000-00.0000.0.00.0001"
              />
            </Form.Item>

            <Divider orientation="left">Segredo de Justiça (opcional)</Divider>

            <Form.Item name="useSecret" valuePropName="checked">
              <Checkbox>Usar credenciais de segredo de justiça</Checkbox>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.useSecret !== currentValues.useSecret}
            >
              {({ getFieldValue }) =>
                getFieldValue('useSecret') ? (
                  <Row gutter={16}>
                    <Col xs={24} sm={12} md={6}>
                      <Form.Item
                        name="arn"
                        label="ARN"
                        rules={[{ required: true, message: 'Digite o ARN' }]}
                      >
                        <Input placeholder="ARN" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Form.Item
                        name="codigoCredentialOrgaoSistema"
                        label="Código Credencial"
                        rules={[{ required: true, message: 'Digite o código' }]}
                      >
                        <Input placeholder="Código" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Form.Item
                        name="login"
                        label="Login"
                        rules={[{ required: true, message: 'Digite o login' }]}
                      >
                        <Input placeholder="Login" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Form.Item
                        name="sistema"
                        label="Sistema"
                        rules={[{ required: true, message: 'Digite o sistema' }]}
                      >
                        <Input placeholder="Sistema" />
                      </Form.Item>
                    </Col>
                  </Row>
                ) : null
              }
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={submitting}
                size="large"
              >
                Cadastrar Demanda
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Text strong>JSON:</Text>
              <div style={{ marginTop: 8 }}>
                <TextArea
                  rows={20}
                  value={jsonValue}
                  onChange={(e) => {
                    setJsonValue(e.target.value);
                    setJsonError(null);
                  }}
                  placeholder={`{\n  "environment": "saj",\n  "idArtifact": 0,\n  "method": {\n    "action": "insert",\n    "idCallback": "callback-id",\n    "updateFrequency": "never",\n    "values": ["0000000-00.0000.0.00.0000"]\n  }\n}`}
                  style={{ fontFamily: 'monospace' }}
                />
              </div>
              {jsonError && (
                <Alert
                  message="Erro no JSON"
                  description={jsonError}
                  type="error"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </div>
            <Space>
              <Button onClick={copyExample} icon={<CopyOutlined />}>
                Copiar exemplo
              </Button>
              <Button onClick={beautifyJson}>Formatar JSON</Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSubmitJson}
                loading={submitting}
              >
                Cadastrar Demanda
              </Button>
            </Space>
          </Space>
        )}
      </Space>
    </Card>
  );
};

/**
 * Componente principal da página DatateamNovo.
 */
const DatateamNovo: React.FC = () => {
  return (
    <DatateamNovoContent />
  );
};

export default DatateamNovo;
